/**
 * Quote-summary money rules, mirrored from gatherco-api-v2 so a quote shows the same
 * figures on the create / copy screens as it does once saved — and as the customer link
 * and the PDF, which both read the summary the server stores.
 *
 *   roundPrice          ↔ numberFormatPrice      (api-v2 utils/helpers/commonFunctions.ts)
 *   gstOn               ↔ getGST                 (api-v2 utils/helpers/quoteSummaryFunctions.ts)
 *   depositAndFinalDue  ↔ getDepositAndFinalDue  (api-v2 utils/helpers/quoteSummaryFunctions.ts)
 *
 * The screens used to keep GST and the total unrounded and round each figure separately
 * for display. On a half-cent total that showed a Total the server never stores, and a
 * "100%" deposit a cent away from it: $0.01 (or -$0.01) left as the final payment.
 * Change a rule on one side and the other must follow.
 */

/** Two decimals, the way the server rounds every stored money field. */
export const roundPrice = (amount: number): number => +(Number(amount) || 0).toFixed(2);

/**
 * GST on an amount, truncated to the cent rather than rounded — the server does this
 * to match Unleashed, so GST on 77.45 is 7.74, not 7.75.
 */
export const gstOn = (taxable: number, percentage: number): number =>
  roundPrice(Math.floor((((Number(taxable) || 0) * percentage) / 100) * 100) / 100);

/**
 * Split an amount into the deposit and the balance after it. The deposit is rounded and
 * the balance derived from the ROUNDED deposit, so the two always add back up to the
 * amount: at 100% the final payment is exactly 0, and it is never negative.
 */
export const depositAndFinalDue = (base: number, percentage: number): { deposit: number; finalDue: number } => {
  const amount = Number(base) || 0;
  const deposit = roundPrice((amount * (Number(percentage) || 0)) / 100);
  return { deposit, finalDue: roundPrice(amount - deposit) };
};
